import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { isEmpty, get } from "lodash";
import { push } from "store/actions/router";

import Alert from "components/Alert";
import IconSpinner from "components/IconSpinner";
import ProgressBar from "components/ProgressBar";
import ContributionSelector from "components/ContributionSelector";
import WorkerRecordPicker from "components/WorkerRecordPicker";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import {
  getInvestmentProfile,
  getPaychexWorkerRecord,
  getPaychexWorkerRecords,
  getUserState,
} from "actions/userActions";
import { getAllAccounts } from "actions/accountActions";
import {
  getContributionRecommendations,
  makeOnboardingPaycheckSplitContribution,
} from "actions/contributionActions";
import {
  accountConstants,
  contributionConstants,
  userConstants,
} from "actions/types";
import { scrollToTop } from "utils/dom";
import {
  getIraAccountIdSelector,
  paychexWorkerRecordsSelector,
  selectedPaychexWorkerRecordSelector,
} from "store/selectors/user";

import "./SetupContribution.scss";
import { IndividualPayrollContributionPending } from "statics/states";
import { INVESTMENT_PROFILE } from "statics/onboardingSteps";
import { isSplitEligibleWorkerRecord } from "utils/paychexWorkerRecords";
import type { RootState } from "types/state";
import type { PaychexWorkerRecord } from "types/models/paychexWorkerRecord";

const actions = [
  userConstants.PAYCHEX_WORKER_RECORDS,
  userConstants.PAYCHEX_WORKER_RECORD,
  accountConstants.GET_ACCOUNTS,
  userConstants.GET_INVESTMENT_PROFILE,
  contributionConstants.ONBOARDING_PAYCHECK_SPLIT_CONTRIBUTION,
  contributionConstants.GET_CONTRIBUTION_RECOMMENDATIONS,
  userConstants.USER_STATE,
];

const isSubmitting = createLoadingSelector(
  contributionConstants.ONBOARDING_PAYCHECK_SPLIT_CONTRIBUTION
);
// Loading between picking a worker record and the selector being ready: the
// per-worker fetch (resolves payrollFrequency) then the recommendations fetch.
const isSelectingWorker = createLoadingSelector([
  userConstants.PAYCHEX_WORKER_RECORD,
  contributionConstants.GET_CONTRIBUTION_RECOMMENDATIONS,
]);
const errorSelector = createErrorSelector(actions);

// `recommendations` is typed as `object` in ContributionState; the reducer
// stores the GetRecommendedContributionAmounts query result, so read the keys
// the component consumes through a local view of that shape.
interface RecommendedContribution {
  recommended: number;
  other: number[];
  isMaxContributionOption?: boolean;
}

interface ContributionRecommendations {
  daily?: RecommendedContribution;
  perPayPeriod?: RecommendedContribution;
}

const mapStateToProps = (state: RootState) => {
  const { dailyMax, perPayPeriodMax, recommendations } = state.contribution;
  return {
    error: errorSelector(state),
    dailyMax,
    perPayPeriodMax,
    dailyRecommendations: (recommendations as ContributionRecommendations)
      .daily,
    perPayPeriodRecommendations: (
      recommendations as ContributionRecommendations
    ).perPayPeriod,
    workerRecords: paychexWorkerRecordsSelector(state),
    selectedWorkerRecord: selectedPaychexWorkerRecordSelector(state),
    accountId: getIraAccountIdSelector(state),
    isSubmitting: isSubmitting(state),
    isSelectingWorker: isSelectingWorker(state),
    userState: get(state, "user.userState.state", ""),
  };
};

const mapDispatchToProps = {
  push,
  getPaychexWorkerRecords,
  getPaychexWorkerRecord,
  getInvestmentProfile,
  getContributionRecommendations,
  getUserState,
  makeOnboardingPaycheckSplitContribution,
  getAllAccounts,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  initialFetching: boolean;
  selectedWorkerRecordId: string | null;
}

// Paychex Flex (Perks) contribution step. Mirrors SetupContribution but the
// schedule frequency comes from the chosen worker record's payroll schedule
// (not a bank or employer group), and the deduction is a PAYCHECK_SPLIT.
class PerksSetupContribution extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      initialFetching: true,
      selectedWorkerRecordId: null,
    };
  }

  async componentDidMount() {
    window.analytics.page("Setup Paycheck Split");
    scrollToTop();
    const client = this.props.client as ApolloClient<object>;
    await this.props.getAllAccounts(client);
    const workersResult = await this.props.getPaychexWorkerRecords(client);
    await this.props.getInvestmentProfile(client);

    const records = (
      get(workersResult, "data", []) as PaychexWorkerRecord[]
    ).filter(isSplitEligibleWorkerRecord);
    // Auto-select when there is exactly one worker record; otherwise the picker
    // is shown and selection drives the recommendations fetch.
    if (records.length === 1) {
      await this._selectWorkerRecord(records[0]);
    }

    this.setState({ initialFetching: false });
  }

  _selectWorkerRecord = async (workerRecord: PaychexWorkerRecord) => {
    this.setState({ selectedWorkerRecordId: workerRecord.id });
    const client = this.props.client as ApolloClient<object>;
    // The list query leaves payrollFrequency null — fetch the full record to
    // resolve payrollFrequency (and companyId) for the schedule.
    const result = await this.props.getPaychexWorkerRecord(
      client,
      workerRecord.id
    );
    const fullRecord = get(result, "data") as PaychexWorkerRecord | undefined;
    // Recommendations key off the resolved payroll frequency.
    await this.props.getContributionRecommendations(client, {
      payrollFrequency: fullRecord?.payrollFrequency,
    });
  };

  // The full record fetched on selection (with resolved payrollFrequency).
  // Guarded on the selected id so a stale record from a prior selection isn't
  // shown while a new fetch is in flight.
  _selectedWorkerRecord = () => {
    const record = this.props.selectedWorkerRecord;
    if (record && record.id === this.state.selectedWorkerRecordId) {
      return record;
    }
    return undefined;
  };

  _submitPaycheckSplit = async (amount: number, isDailyCustom: boolean) => {
    const workerRecordId = this.state.selectedWorkerRecordId;
    if (!workerRecordId) {
      return;
    }
    try {
      await this.props.makeOnboardingPaycheckSplitContribution(
        this.props.client as ApolloClient<object>,
        {
          workerRecordId,
          accountId: this.props.accountId as string,
          amount: +amount,
          isDailyCustom,
        }
      );
      await this.props.getUserState(this.props.client as ApolloClient<object>);
      window.analytics.track("Submitted Paycheck Split");
      this.props.push("/dashboard");
    } catch (error) {
      return error;
    }
  };

  render() {
    const { workerRecords } = this.props;
    const selectedRecord = this._selectedWorkerRecord();
    const hasSelection = !!this.state.selectedWorkerRecordId;
    const noErrorNotLoading = !this.props.error && !this.state.initialFetching;
    const isReadyForSelector =
      !!selectedRecord && !isEmpty(this.props.perPayPeriodRecommendations);
    const shouldShowPicker =
      noErrorNotLoading && !hasSelection && workerRecords.length > 1;
    const shouldShowSelector = noErrorNotLoading && isReadyForSelector;
    // Spinner while the initial fetch runs, and in the gap between selecting a
    // worker and the selector being ready (per-worker + recommendations fetch).
    const shouldShowLoader =
      !this.props.error &&
      (this.state.initialFetching ||
        (hasSelection && !isReadyForSelector && this.props.isSelectingWorker));
    const shouldShowProgressBar =
      this.props.userState === IndividualPayrollContributionPending;

    return (
      <>
        {shouldShowProgressBar && (
          <ProgressBar
            isEmployer={false}
            activeStepId={INVESTMENT_PROFILE.id}
            progressPercent={"90"}
          />
        )}
        <div id="setup-contributions">
          <div className="mega-container">
            <div className="main-content">
              {shouldShowLoader && <IconSpinner centered />}
              {this.props.error && (
                <Alert type="error" msg={this.props.error} />
              )}
              {shouldShowPicker && (
                <WorkerRecordPicker
                  workerRecords={workerRecords}
                  onSelect={this._selectWorkerRecord}
                />
              )}
              {shouldShowSelector && (
                <ContributionSelector
                  // @ts-expect-error redux ContributionState types this as
                  // number | null; the selector passes the raw value through.
                  dailyMax={this.props.dailyMax}
                  // @ts-expect-error redux ContributionState types this as
                  // number | null; the selector passes the raw value through.
                  perPayPeriodMax={this.props.perPayPeriodMax}
                  dailyRecommendations={this.props.dailyRecommendations}
                  perPayPeriodRecommendations={
                    this.props.perPayPeriodRecommendations
                  }
                  error={this.props.error}
                  onSubmit={this._submitPaycheckSplit}
                  isSubmitting={this.props.isSubmitting}
                  schedule={{
                    frequency: selectedRecord!.payrollFrequency ?? "",
                  }}
                  isPaycheck={true}
                >
                  <section className="page-title-wrap">
                    <article className="text-cell">
                      <h1 className="page-title">
                        With Icon, you have the freedom to choose how much of
                        your monthly pay you set aside to your retirement
                        account.
                      </h1>
                      <p className="page-subtext">
                        Please select the amount that{"'"}s good for you or
                        enter in a custom amount. You can modify your
                        contribution amount at any time.
                      </p>
                    </article>
                  </section>
                </ContributionSelector>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default connector(withApollo<Props>(PerksSetupContribution));
